import React, { useState, useEffect } from 'react';
import { db, auth } from '../../config/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import './AdvertisingManagement.css';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const MAX_IMAGE_SIZE = 1000000; // 1MB
const MAX_WIDTH = 1920; // Banner width
const MAX_HEIGHT = 400; // Banner height
const COMPRESSION_QUALITY = 0.8; // 80% quality

const AdvertisingManagement = () => {
  const [ads, setAds] = useState([]);
  const [newAd, setNewAd] = useState({
    title: '',
    description: '',
    link: '',
    imageUrl: '',
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject('No file provided');
        return;
      }

      if (!file.type.startsWith('image/')) {
        reject('File must be an image');
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate dimensions to maintain aspect ratio 16:5 (banner format)
          const aspectRatio = MAX_WIDTH / MAX_HEIGHT;
          const imageRatio = width / height;

          if (imageRatio > aspectRatio) {
            // Image is wider than needed
            height = MAX_HEIGHT;
            width = height * imageRatio;
          } else {
            // Image is taller than needed
            width = MAX_WIDTH;
            height = width / imageRatio;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          
          // Center the image
          const x = (canvas.width - width) / 2;
          const y = (canvas.height - height) / 2;
          
          ctx.drawImage(img, x, y, width, height);

          const compressedDataUrl = canvas.toDataURL('image/jpeg', COMPRESSION_QUALITY);
          
          // Size check
          const base64str = compressedDataUrl.split(',')[1];
          const decoded = atob(base64str);
          const finalSize = decoded.length;

          if (finalSize > MAX_IMAGE_SIZE) {
            reject(`Image still too large. Please use a smaller image (max ${MAX_IMAGE_SIZE/1000}KB)`);
            return;
          }

          resolve(compressedDataUrl);
        };
        img.onerror = (error) => reject('Error loading image');
      };
      reader.onerror = (error) => reject('Error reading file');
    });
  };

  useEffect(() => {
    const checkAdminAndLoadAds = async () => {
      const user = auth.currentUser;
      if (!user) {
        setMessage('You must be logged in to view advertisements');
        return;
      }

      if (user.email !== 'admin@aroundu.co.za') {
        setMessage('Insufficient permissions: Admin access required');
        return;
      }

      loadAds();
    };

    checkAdminAndLoadAds();
  }, []);

  const loadAds = async () => {
    try {
      const adsSnapshot = await getDocs(collection(db, 'advertisements'));
      const adsData = adsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAds(adsData);
    } catch (error) {
      console.error('Error loading ads:', error);
      setMessage('Error loading advertisements: ' + error.message);
    }
  };

  const handleInputChange = async (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'image') {
      const file = files[0];
      if (file) {
        try {
          setLoading(true);
          setMessage('');
          
          // Initial size check
          if (file.size > MAX_IMAGE_SIZE * 2) {
            throw new Error(`Image too large. Please select an image under ${MAX_IMAGE_SIZE/1000}KB`);
          }

          const compressedImage = await compressImage(file);
          setNewAd(prev => ({
            ...prev,
            imageUrl: compressedImage
          }));
          setImagePreview(compressedImage);
          setMessage({ text: 'Image processed successfully', type: 'success' });
        } catch (error) {
          setMessage({ text: error.message || 'Error processing image', type: 'error' });
          setImagePreview(null);
          e.target.value = ''; // Reset file input
        } finally {
          setLoading(false);
        }
      }
    } else {
      setNewAd(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setNewAd({
      title: ad.title,
      description: ad.description || '',
      link: ad.link || '',
      imageUrl: ad.imageUrl,
      startDate: ad.startDate,
      endDate: ad.endDate
    });
    setImagePreview(ad.imageUrl);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const user = auth.currentUser;
      if (!user || user.email !== 'admin@aroundu.co.za') {
        throw new Error('Insufficient permissions');
      }

      if (!newAd.imageUrl) {
        throw new Error('Please select an image');
      }

      if (editingAd) {
        // Update existing ad
        await updateDoc(doc(db, 'advertisements', editingAd.id), {
          ...newAd,
          updatedAt: new Date().toISOString(),
          updatedBy: user.uid
        });
        setMessage({ text: 'Advertisement updated successfully!', type: 'success' });
      } else {
        // Add new ad
        await addDoc(collection(db, 'advertisements'), {
          ...newAd,
          createdAt: new Date().toISOString(),
          createdBy: user.uid,
          updatedAt: new Date().toISOString(),
          updatedBy: user.uid
        });
        setMessage({ text: 'Advertisement added successfully!', type: 'success' });
      }

      // Reset form
      setNewAd({
        title: '',
        description: '',
        link: '',
        imageUrl: '',
        startDate: '',
        endDate: ''
      });
      setImagePreview(null);
      setEditingAd(null);
      loadAds();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAd(null);
    setNewAd({
      title: '',
      description: '',
      link: '',
      imageUrl: '',
      startDate: '',
      endDate: ''
    });
    setImagePreview(null);
    setMessage('');
  };

  const handleDelete = async (ad) => {
    if (window.confirm('Are you sure you want to delete this advertisement?')) {
      try {
        const user = auth.currentUser;
        if (!user || user.email !== 'admin@aroundu.co.za') {
          throw new Error('Insufficient permissions');
        }

        // Delete ad from Firestore
        await deleteDoc(doc(db, 'advertisements', ad.id));
        
        setMessage('Advertisement deleted successfully');
        loadAds();
      } catch (error) {
        console.error('Error deleting ad:', error);
        setMessage('Error deleting advertisement: ' + error.message);
      }
    }
  };

  const handleDeleteClick = (ad) => {
    setAdToDelete(ad);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await handleDelete(adToDelete);
      setDeleteModalOpen(false);
      setAdToDelete(null);
    } catch (error) {
      console.error('Error deleting advertisement:', error);
    }
  };

  if (!auth.currentUser || auth.currentUser.email !== 'admin@aroundu.co.za') {
    return (
      <div className="advertising-management">
        <div className="message error">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="advertising-management">
      <h2>Advertising Management</h2>

      <form onSubmit={handleSubmit} className="ad-form">
        <h3>{editingAd ? 'Edit Advertisement' : 'Add New Advertisement'}</h3>
        
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            name="title"
            value={newAd.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={newAd.description}
            onChange={handleInputChange}
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>Link URL</label>
          <input
            type="url"
            name="link"
            value={newAd.link}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Advertisement Image *</label>
          <input
            type="file"
            name="image"
            onChange={handleInputChange}
            accept="image/jpeg,image/png,image/gif"
            required={!newAd.imageUrl}
          />
          <p className="helper-text">
            Recommended image dimensions: 1920x400 pixels. Images will be optimized for banner display.
          </p>
          {loading && <div className="loading">Processing image...</div>}
          {imagePreview && (
            <div className="image-preview">
              <img 
                src={imagePreview} 
                alt="Preview"
              />
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={newAd.startDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>End Date *</label>
            <input
              type="date"
              name="endDate"
              value={newAd.endDate}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-buttons">
          <button type="submit" disabled={loading}>
            {loading ? (editingAd ? 'Updating...' : 'Adding...') : (editingAd ? 'Update Advertisement' : 'Add Advertisement')}
          </button>
          
          {editingAd && (
            <button 
              type="button" 
              onClick={handleCancelEdit}
              className="cancel-btn"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="ads-list">
        <h3>Current Advertisements</h3>
        <div className="ads-grid">
          {ads.map(ad => {
            // Add date formatting
            const startDate = new Date(ad.startDate).toLocaleDateString();
            const endDate = new Date(ad.endDate).toLocaleDateString();
            const isExpired = new Date(ad.endDate) < new Date();
            
            return (
              <div key={ad.id} className={`ad-card ${isExpired ? 'expired' : ''}`}>
                <div className="ad-image">
                  <img src={ad.imageUrl} alt={ad.title} />
                </div>
                <div className="ad-details">
                  <h4>{ad.title}</h4>
                  <p>{ad.description}</p>
                  <p className="dates">
                    Active period: {startDate} - {endDate}
                  </p>
                  {isExpired && <span className="expired-badge">Expired</span>}
                </div>
                <div className="button-wrapper">
                  <div className="button-container">
                    <button className="card-edit-btn" onClick={() => handleEdit(ad)}>
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button className="card-delete-btn" onClick={() => handleDeleteClick(ad)}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={adToDelete?.title || 'this advertisement'}
      />
    </div>
  );
};

export default AdvertisingManagement; 